import React, { useEffect } from 'react';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import { useForm } from 'react-hook-form';
import FormInputText from '@app/component/form/input/text/FormInputText';
import FormInputPhone from '@app/component/form/input/phone/FormInputPhone';
import { Switch } from '@headlessui/react';
import FormSwitch from '@app/component/form/input/switch/FormSwitch';
import FormButton from '@app/component/form/button/FormButton';
import CountryAutocompleteField from '@app/component/form/input/autocomplete/CountryAutocompleteField';
import CompanyRegistrationInput from '@app/model/registration/input/CompanyRegistrationInput';
import Principal from '@app/model/authentication/Principal';
import { useMutation } from '@tanstack/react-query';
import RegistrationService from '@app/model/registration/service/RegistrationService';

const RegisterCompanyFlowPage: React.FunctionComponent<{
    principal: Principal,
    onComplete: (principal: Principal) => any
}> = ({principal, onComplete}) => {

    const form = useForm();
    const {t} = useTranslation();
    const toaster = useToaster();

    const hasAcceptedTerms = form.watch('acceptTerms');


    useEffect(() => {
        form.setValue("profileId", principal.id)
        form.setValue("name", principal.name)
        form.setValue("email", principal.username)
    }, []);

    const registerCompanyMutation = useMutation({
        mutationFn: RegistrationService.registerCompany,
        onSuccess: (data) => onComplete(data),
        onError: (error: string) => {
            toaster.error('Registration', t(error))
        }
    })

    const onSubmit = (data: CompanyRegistrationInput) => {
        registerCompanyMutation.mutate({
            profileId: data.profileId,
            phone: data.phone,
            companyName: data.companyName,
            countryId: data.countryId,
            acceptTerms: data.acceptTerms,
        })
    }

    return <div className="min-h-full bg-white">
        <div className="flex min-w-max h-dvh content-center justify-center flex-1">
            <div className="flex flex-1 flex-col justify-center px-6 py-12">
                <div className="mx-auto w-full max-w-sm lg:w-96">
                    <div>
                        <img
                            className="h-10 w-auto"
                            src="/assets/img/brand/logo-icon.png"
                            alt="RoomPulse"
                        />
                        <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                            Finish up your account
                        </h2>
                    </div>

                    <div className="mt-10">
                        <div>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                <div className="grid grid-cols-1 gap-y-4 gap-x-8">
                                    <input type="hidden" {...form.register("profileId")}/>
                                    <FormInputText name='name' form={form} label='Name' isRequired
                                                   placeholder="Insert your name" disabled/>
                                    <FormInputText name='email' form={form} placeholder="Insert your email"
                                                   label='Email' isRequired disabled/>
                                    <FormInputText name='companyName' form={form} label='Company'
                                                   placeholder="Insert your company name" isRequired/>
                                    <CountryAutocompleteField name="countryId" form={form} label="Country" isRequired
                                                              placeholder="Insert your country"/>

                                    <FormInputPhone namePrefix='phone' form={form} label='Phone Number' isRequired
                                                    placeholder="Insert your phone number"/>
                                </div>


                                <div className="mt-8">
                                    <Switch.Group as="div" className="flex gap-x-4 sm:col-span-2 mt-3">
                                        <div className="flex h-6 items-center">
                                            <FormSwitch name='acceptTerms' form={form}
                                                        label='Agree to the policy'/>
                                            <Switch.Label className="text-sm leading-6 text-slate-600 ml-2">
                                                By selecting this, you agree to our{' '}
                                                <a href="/sdsd"
                                                   className="font-semibold text-blue-600 hover:text-blue-500">privacy&nbsp;policy</a>.
                                            </Switch.Label>
                                        </div>
                                    </Switch.Group>
                                    <FormButton type='submit' state='primary' extraClassName='flex w-full mt-4'
                                                isLoading={registerCompanyMutation.isPending}
                                                disabled={!hasAcceptedTerms}>
                                        Sign up</FormButton>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div className="relative hidden flex-1 lg:block">
                <img
                    className="absolute inset-0 h-full w-full object-cover"
                    src="https://images.unsplash.com/photo-1546967900-1bea5f16b69d?q=80&w=2670&auto=format&fit=[…]3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    alt=""
                />
            </div>
        </div>
    </div>
}

export default RegisterCompanyFlowPage;